/**
 * Section 09b — OM_Simple Strategy.
 *
 * The CLN colour-flip rule. A SIBLING of the Om chart strategy, not a version of
 * it, and this page says so explicitly — Om's measured record belongs to a
 * different rule and must not be read across.
 *
 * The rule itself lives in utils/strategies/omSimpleEngine.ts, a byte-identical
 * twin of backend/services/strategies/omSimpleEngine.ts.
 */
import React from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import InvertColorsIcon from '@mui/icons-material/InvertColors';
import GuideCallout from '../GuideCallout';
import OpenInApp from '../OpenInApp';
import { GuideSection } from './types';

const H = ({ id, children }: { id: string; children: React.ReactNode }) => (
  <Typography id={`anchor-${id}`} variant="h5" sx={{ fontWeight: 700, mt: 4, mb: 1.25, scrollMarginTop: 80 }}>{children}</Typography>
);
const P = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="body1" sx={{ mb: 1.25, lineHeight: 1.65, fontSize: '0.95rem' }}>{children}</Typography>
);

const Section_strategy_omsimple: GuideSection = {
  id: 'strategy-omsimple',
  label: 'OM_Simple Strategy',
  group: 'strategies',
  icon: <InvertColorsIcon fontSize="small" />,
  render: () => (
    <>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>OM_Simple Strategy</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        One rule, stated in full: <strong>when the CLN candle colour turns red → green, that is a BUY; when it
        turns green → red, that is a SHORT.</strong> Nothing else. No exits, no stop of its own, no auto-trade.
      </Typography>

      <GuideCallout kind="warning" title="OM_Simple is not Om">
        Om is a different strategy — an AV_Band regime flip with a Daily CLN gate, an MM trailing stop and a
        30-bar time stop. Its published numbers (+0.92%/trade, PF 1.38, 35 of 48 symbols) describe <em>that</em>{' '}
        rule and say nothing about this one. OM_Simple was added as a sibling precisely so Om's evidence base
        was not silently reused. Om is unchanged.
      </GuideCallout>

      <H id="rule">The rule</H>
      <P>
        CLN colours every candle by its <strong>linear regression</strong>, never by the candle's own direction:
        green when the regression of the CLOSES sits at or above the regression of the OPENS, red otherwise.
        That is why a red-bodied candle can print green — the disagreement is the information the candle type
        exists to show.
      </P>
      <P>
        OM_Simple fires on the bar where that colour <strong>changes</strong>. It is always in the market once
        the first signal fires: a BUY is closed only by the next SHORT, and vice versa.
      </P>

      <GuideCallout kind="warning" title="There are no exits — that is the design, and it is the risk">
        The strategy will never tell you to close a position. It has no stop, no target and no time exit, so an
        open trade rides until the colour flips back, however far that is. <strong>Place your own stop and
        target on the chart</strong> once you have confirmed the signal by eye. For the same reason the scan
        engine <strong>refuses auto-trade outright</strong> — the per-schedule Auto-Trade switch has no effect
        on OM_Simple, and no schedule can arm it.
      </GuideCallout>

      <H id="length">The regression length, and why the default is 50</H>
      <P>
        Two lengths matter and they are separate settings. <strong>Chart Settings → CLN</strong> sets the length
        the chart <em>draws</em> (default 14). <strong>Chart Settings → OM_Simple</strong> sets the length the
        strategy <em>evaluates</em> (default 50). When they differ the chart shows a warning, because the markers
        would otherwise sit on colour changes you cannot see.
      </P>
      <P>
        The default is 50 rather than 14 because of what the shorter length costs. Measured over 10 liquid
        majors, March 2024 – September 2026, 0.20% round-trip cost, with every timeframe built from a single
        hourly base so they compare directly:
      </P>
      <Box sx={{ overflowX: 'auto', mb: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>Bar</strong></TableCell>
              <TableCell><strong>Length</strong></TableCell>
              <TableCell align="right"><strong>Signals / month / symbol</strong></TableCell>
              <TableCell align="right"><strong>Net per trade</strong></TableCell>
              <TableCell align="right"><strong>Profit factor</strong></TableCell>
              <TableCell align="right"><strong>Symbols positive</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow><TableCell>4H</TableCell><TableCell>14</TableCell><TableCell align="right">33.6</TableCell><TableCell align="right">−0.147%</TableCell><TableCell align="right">0.88</TableCell><TableCell align="right">1 / 10</TableCell></TableRow>
            <TableRow><TableCell>4H</TableCell><TableCell>50</TableCell><TableCell align="right">17.7</TableCell><TableCell align="right">−0.054%</TableCell><TableCell align="right">0.96</TableCell><TableCell align="right">3 / 10</TableCell></TableRow>
            <TableRow sx={{ '& td': { fontWeight: 700 } }}><TableCell>7H</TableCell><TableCell>14</TableCell><TableCell align="right">22.5</TableCell><TableCell align="right">−0.125%</TableCell><TableCell align="right">0.91</TableCell><TableCell align="right">1 / 10</TableCell></TableRow>
            <TableRow sx={{ '& td': { fontWeight: 700 } }}><TableCell>7H</TableCell><TableCell>50</TableCell><TableCell align="right">11.3</TableCell><TableCell align="right">+0.045%</TableCell><TableCell align="right">1.03</TableCell><TableCell align="right">7 / 10</TableCell></TableRow>
            <TableRow><TableCell>8H</TableCell><TableCell>50</TableCell><TableCell align="right">8.8</TableCell><TableCell align="right">−0.096%</TableCell><TableCell align="right">0.95</TableCell><TableCell align="right">4 / 10</TableCell></TableRow>
            <TableRow><TableCell>1D</TableCell><TableCell>50</TableCell><TableCell align="right">3.1</TableCell><TableCell align="right">+0.537%</TableCell><TableCell align="right">1.17</TableCell><TableCell align="right">8 / 10</TableCell></TableRow>
          </TableBody>
        </Table>
      </Box>
      <GuideCallout kind="warning" title="Read this as break-even at best">
        At the chart's drawing default of 14 the rule <strong>loses to costs</strong>. At 50 on 7H it is roughly
        break-even — a profit factor of 1.03 is not an edge, it is noise with a positive sign. Treat OM_Simple
        as a way to be <em>told when the colour changed</em>, not as a system that makes money on its own.
        <br /><br />
        These figures are a single-pass measurement. They are <strong>not</strong> a pre-registered study with a
        train/test split, ablations and breadth gates of the kind behind Bhairav's and Om's numbers, so do not
        compare them to those directly.
      </GuideCallout>

      <H id="intervals">Intervals, including 7H</H>
      <P>
        OM_Simple runs on any interval the chart can draw, including custom ones you define in{' '}
        <strong>Charts → interval picker → Custom intervals</strong>. Adding it there is all that is needed —
        it appears immediately in the Strategies page's Interval and Schedule pickers, for ad-hoc scans,
        schedules and auto-trade. There is no second opt-in.
      </P>
      <P>
        Two settings on a schedule are easy to confuse and they are not the same thing.{' '}
        <strong>Interval</strong> is the CANDLE — the bar the rule is evaluated on.{' '}
        <strong>Check every N units</strong> is the CLOCK — how often the scanner looks. For an interval that
        divides the day they can be equal. For one that does not, they cannot be, and the scanner simply
        looks more often than it finds: it only ever evaluates closed bars and logs at most one signal per
        bar. The app fills in the right cadence when you pick the interval and the watchlist.
      </P>
      <P>
        A cadence and a bar size are separate things. 420 minutes does not divide a day (1440 ÷ 420 = 3.43), so
        the scheduler cannot have a "every 7 hours" cadence. Instead the schedule <strong>ticks on a finer grid
        that contains every 7H close</strong> — the app fills in the right one when you pick the interval — and
        the engine evaluates <strong>closed bars only</strong> and logs at most one signal per bar. The extra
        ticks cost a data refresh and nothing else.
      </P>
      <GuideCallout kind="info" title="A 7H day is 7H + 7H + 7H + 3H">
        The bucket grid restarts at IST midnight, so the last bar of each day is a short one. That is exactly
        what the chart draws and exactly what the scan reads — the rule file is a byte-identical twin across the
        two sides, enforced in CI — so a signal in the Signal Log always lands on the bar the chart marks.
      </GuideCallout>

      <H id="repaint">Why a marker appears only after the bar closes</H>
      <P>
        A linear regression over the last N bars moves on every tick of the <em>forming</em> bar, so the
        right-most candle's colour can flip back and forth and settle somewhere else at the close. The chart
        paints that live colour deliberately, but OM_Simple never marks it: signals are decided on closed bars
        only. A closed bar's colour can never change afterwards, because the regression at bar <em>i</em> reads
        only bars up to <em>i</em>.
      </P>

      <H id="markers">The three marks: B / S, W and C</H>
      <P>
        Only the arrows are trades. The two circles answer the question this strategy otherwise
        leaves open — <em>should I keep holding this?</em>
      </P>
      <P>
        <strong>▲ B / ▼ S</strong> — the trade. The bar where the CLN colour changed.
      </P>
      <P>
        <strong>W — the leg you are in is weakening.</strong> Reversal pressure against your open
        position crossed its threshold. It is coloured and placed for the direction a flip{' '}
        <em>would</em> go, so while you are long it is a <strong>red W above</strong> the bar. It is
        not an exit and not a trade — the stop is still yours to place.
      </P>
      <P>
        <strong>C — the leg you are in is resuming.</strong> Trend pressure for the position crossed
        its threshold <em>and</em> the higher timeframe agrees. Coloured for the direction you already
        hold. Because OM_Simple is always in the market, a continuation can only ever point the way
        you are already positioned — so it is a reason to keep holding, never a new entry.
      </P>
      <P>
        Both are edge-triggered: one mark per episode, not one per bar. Hover any of them and the
        crosshair explains it and shows the score that fired it.
      </P>
      <GuideCallout kind="info" title="Why the thresholds are not the ones in the TradingView original">
        The marks are ported from an LR multi-timeframe reversal/continuation indicator, whose
        formulas are used verbatim. Its thresholds are not. Measured across 48 symbols on 4H bars,
        its watch level of 55 sits at the <strong>98th percentile</strong> of this data and its
        confirm level of 70 at the <strong>96th</strong> — copying them would draw almost nothing,
        partly because that indicator uses a 21-bar regression where OM_Simple uses 50, which
        flattens every score. The shipped levels were chosen from a measured firing rate instead:
        about <strong>3.5 W and 5 C per 100 bars</strong>. Both are adjustable in Chart Settings.
      </GuideCallout>
      <P>
        Is &ldquo;watch&rdquo; an earned word? Measured bar-for-bar over the same 48 symbols, a colour
        flip follows within 10 bars <strong>70%</strong> of the time after a W, against a{' '}
        <strong>45%</strong> base rate — and that holds across every leg-age bucket, so it is not
        simply that longer legs collect more marks. That figure is reported, never tuned against:
        fitting a chart annotation to what happens next would quietly turn it into a trade.
      </P>
      <GuideCallout kind="warning" title="A C needs a higher timeframe, and says so when it has none">
        The continuation mark checks the regression direction on the next timeframe up — the same
        one the CLN context band draws, resolved automatically from your interval (4H reads the
        daily, 7H reads the daily, 12h reads the weekly). On an interval with nothing above it, or
        before enough higher-timeframe history has loaded, <strong>C is hidden entirely</strong> and
        the chart says so. It is never quietly downgraded to an ungated version — that would be a
        different claim wearing the same letter.
      </GuideCallout>

      <H id="use">Using it</H>
      <P>
        On <OpenInApp to="/charts" label="Charts" />, pick <strong>CLN</strong> as the candle type and{' '}
        <strong>OM_Simple</strong> in the Strategies dropdown. Green ▲ B markers are BUYs, red ▼ S markers are
        SHORTs, and the W / C circles are the hold-or-not marks described above; hovering any of them
        explains it in the crosshair tooltip. On{' '}
        <OpenInApp to="/strategies" label="Strategies" />, select the <strong>OM_Simple</strong> card to run an
        ad-hoc scan or add a schedule with Dashboard / Telegram routing.
      </P>
      <P>
        A thin-volume filter is available in Chart Settings (skip flips printed on unusually low participation).
        It tested slightly better on expectancy but leaves visible colour changes unmarked, so it ships off.
      </P>
    </>
  ),
};

export default Section_strategy_omsimple;
